import React, { useMemo } from 'react';

import type { ExtraProps } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Streamdown } from 'streamdown';
import { makeStyles } from 'tss-react/mui';

import { counterResetStyles } from './Citation';
import { CitationCardDataContext } from './citation-card-data-context';
import type { CitationCardData } from './governing-document-sources';
import { RelationshipCitation } from './RelationshipCitation';
import { ALINE_STREAMDOWN_PLUGINS } from '../../chat/ChatPanelMarkdown/markdown-plugins';

const useStyles = makeStyles()({
  root: {
    '& > :first-child': {
      marginTop: 0,
    },
    '& > :last-child': {
      marginBottom: 0,
    },
    ...counterResetStyles,
    textAlign: 'left',
  },
});

type MarkdownComponentProps<El extends HTMLElement = HTMLElement> = React.ClassAttributes<El> &
  React.HTMLAttributes<El> &
  ExtraProps;

interface RelationshipReasonMarkdownProps {
  llmReasonsMarkdown: string;
  /** Numbers each `#doc/` badge and backs its card. A citation missing from the map falls back to the CSS counter, with no card. */
  citationCardDataByDocumentId?: ReadonlyMap<string, CitationCardData>;
  /** Merged over the default root styling, so a caller can override typography. */
  className?: string;
  /** Threaded down to the inline citations so a cross-document jump can highlight this row. */
  relationshipId?: string | null;
}

export const RelationshipReasonMarkdown = React.memo(function RelationshipReasonMarkdown({
  citationCardDataByDocumentId,
  className,
  llmReasonsMarkdown,
  relationshipId,
}: RelationshipReasonMarkdownProps): JSX.Element {
  const { classes, cx } = useStyles();
  // Memoized so the anchor component keeps a stable identity across renders (no remount).
  const components = useMemo<React.ComponentProps<typeof Streamdown>['components']>(
    () => ({
      a: props => <RelationshipAnchorElement {...props} relationshipId={relationshipId} />,
    }),
    [relationshipId]
  );

  return (
    <CitationCardDataContext.Provider value={citationCardDataByDocumentId}>
      <Streamdown
        className={cx(classes.root, className)}
        components={components}
        plugins={ALINE_STREAMDOWN_PLUGINS}
        remarkPlugins={[remarkGfm]}
      >
        {llmReasonsMarkdown}
      </Streamdown>
    </CitationCardDataContext.Provider>
  );
});

/**
 * Routes #doc/ links to RelationshipCitation (with the link title as
 * reference text for PDF highlighting), and renders plain links for
 * other URLs. The reference text comes from standard markdown link title
 * syntax: [Citation](#doc/id/ver/id/loc/anchor "quoted text")
 */
function RelationshipAnchorElement({
  href,
  relationshipId,
  title,
  ...rest
}: MarkdownComponentProps<HTMLAnchorElement> & {
  href?: string;
  relationshipId?: string | null;
  title?: string;
}): JSX.Element | null {
  if (href?.startsWith('#doc/'))
    return (
      <RelationshipCitation
        href={href}
        referenceText={title ?? ''}
        relationshipId={relationshipId}
      />
    );

  if (!href) return null;

  return (
    <a href={href} rel="noopener noreferrer" target="_blank" {...rest}>
      {rest.children}
    </a>
  );
}
